import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';

/**
 * LogoPicker Component
 * 
 * Modal for customizing wedding logo and text.
 * Allows users to upload a logo image and customize the wedding text.
 */
function LogoPicker({ onClose, onApply, currentLogo, currentText, onShowToast }) {
    const [logoPreview, setLogoPreview] = useState(currentLogo);
    const [logoFile, setLogoFile] = useState(null);
    const [weddingText, setWeddingText] = useState(currentText);
    const [showCropper, setShowCropper] = useState(false);
    const [imageSrc, setImageSrc] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    // Handle logo file upload
    const handleLogoUpload = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImageSrc(reader.result);
                setShowCropper(true);
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle crop complete
    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    // Create cropped image
    const createCroppedImage = async () => {
        try {
            const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
            setLogoPreview(croppedImage);
            setShowCropper(false);
        } catch (e) {
            console.error(e);
        }
    };

    // Helper function to crop image
    const getCroppedImg = (imageSrc, pixelCrop) => {
        return new Promise((resolve) => {
            const image = new Image();
            image.src = imageSrc;
            image.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                canvas.width = pixelCrop.width;
                canvas.height = pixelCrop.height;

                ctx.drawImage(
                    image,
                    pixelCrop.x,
                    pixelCrop.y,
                    pixelCrop.width,
                    pixelCrop.height,
                    0,
                    0,
                    pixelCrop.width,
                    pixelCrop.height
                );

                canvas.toBlob((blob) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    reader.onloadend = () => {
                        resolve(reader.result);
                    };
                });
            };
        });
    };

    // Handle apply changes
    const handleApply = () => {
        onApply({
            logo: logoPreview,
            text: weddingText,
            file: logoFile,
        });

        // Show success toast
        if (onShowToast) {
            onShowToast({ message: 'Logo and text applied successfully!', type: 'success' });
        }
    };


    // Handle reset to default
    const handleReset = async () => {
        // Import the loader function
        const { loadUserLogoConfig } = require('../utils/userDataLoader');
        const defaults = await loadUserLogoConfig();

        // Update preview only (don't apply or clear localStorage yet)
        setLogoPreview(defaults.logo);
        setWeddingText(defaults.text);
        setLogoFile(null);
    };

    return (
        <>
            <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4"
                onClick={(e) => e.target === e.currentTarget && onClose()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="logo-picker-title"
            >
                <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto custom-scrollbar" onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="sticky top-0 bg-white border-b border-accent px-6 py-4 flex items-center justify-between z-10">
                        <div>
                            <h2 id="logo-picker-title" className="text-2xl font-semibold text-secondary">
                                Customize Logo & Text
                            </h2>
                            <p className="text-sm text-gray-600 mt-1">
                                Upload your wedding logo and customize the text
                            </p>
                        </div>

                        <button onClick={onClose} aria-label="Close logo picker" className="group flex-shrink-0 ml-4">
                            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 transition-all duration-200 group-hover:bg-gray-200 group-hover:scale-105">
                                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </span>
                        </button>
                    </div>

                    {/* Content */}
                    <div className="px-6 py-6">
                        {/* Logo & Text Section */}
                        <section className="mb-8">
                            <h3 className="text-lg font-semibold text-secondary mb-2">
                                Customize Logo & Text
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                                Upload your wedding logo and customize the text that appears below it
                            </p>

                            <div className="space-y-4">
                                {/* Logo Upload */}
                                <div>
                                    <label className="block text-sm font-medium text-secondary mb-2">
                                        Logo Image
                                    </label>
                                    <div className="flex items-center gap-3">
                                        {logoFile && (
                                            <span className="text-sm text-gray-600 flex-1 truncate">
                                                {logoFile.name}
                                            </span>
                                        )}
                                        <label htmlFor="logo-upload" className="px-4 py-2 bg-primary rounded-lg font-medium hover:opacity-90 transition-all duration-200 shadow-sm cursor-pointer text-sm whitespace-nowrap" style={{color: 'var(--color-surface)'}}>
                                            <input type="file" id="logo-upload" accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml" onChange={handleLogoUpload} className="sr-only" />
                                            Choose Logo File
                                        </label>
                                    </div>
                                </div>

                                {/* Wedding Text */}
                                <div>
                                    <label htmlFor="wedding-text" className="block text-sm font-medium text-secondary mb-2">
                                        Wedding Text
                                    </label>
                                    <input
                                        type="text"
                                        id="wedding-text"
                                        value={weddingText}
                                        onChange={(e) => setWeddingText(e.target.value)}
                                        placeholder="Wedding of Groom & Bride"
                                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* Preview Section */}
                        <section className="mb-8 border-t-2 border-gray-300 pt-8">
                            <h3 className="text-lg font-semibold text-secondary mb-2">
                                Preview
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                                Here's how your logo and text will appear on the website
                            </p>

                            <div className="p-8 bg-gray-50 rounded-lg border-2 border-accent">
                                <div className="flex flex-col items-center">
                                    <img src={logoPreview} alt="Logo Preview" className="w-36 h-36 object-contain mb-3 opacity-95" />
                                    <h1 className="text-lg font-medium text-secondary-80 tracking-wide text-center">
                                        {weddingText || 'Wedding of Groom & Bride'}
                                    </h1>
                                </div>
                            </div>
                        </section>

                        {/* Apply Section */}
                        <section className="border-t-2 border-gray-300 pt-8">
                            <h3 className="text-lg font-semibold text-secondary mb-2">
                                Apply Your Logo
                            </h3>
                            <p className="text-sm text-gray-600 mb-6">
                                Click "Apply Changes" to preview your logo and text on the website
                            </p>

                            <div className="space-y-4">
                                {/* Apply and Reset Buttons - One Row */}
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <button
                                        onClick={handleApply}
                                        className="flex-1 px-6 py-3 bg-primary rounded-lg font-medium hover:opacity-90 transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 flex items-center justify-center gap-2" style={{color: 'var(--color-surface)'}}
                                        aria-label="Apply logo changes"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                        Apply Changes
                                    </button>

                                    <button
                                        onClick={handleReset}
                                        className="flex-1 px-6 py-3 bg-neutral text-secondary rounded-lg font-medium hover:bg-neutral/90 transition-all duration-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-neutral/50 focus:ring-offset-2 focus:ring-offset-white flex items-center justify-center gap-2"
                                        aria-label="Reset to default logo and text"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        Reset to Default
                                    </button>
                                </div>

                                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                        Next Steps
                                    </h4>
                                    <ol className="text-sm text-blue-800 space-y-2 ml-1">
                                        <li className="flex gap-2">
                                            <span className="font-semibold flex-shrink-0">1.</span>
                                            <span>Click "Apply Changes" to preview your logo and text on the website</span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="font-semibold flex-shrink-0">2.</span>
                                            <span>Close this modal and click "Export Config" in the header to save all settings</span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="font-semibold flex-shrink-0">3.</span>
                                            <span>Send the exported ZIP file to your developer</span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="font-semibold flex-shrink-0">4.</span>
                                            <span>Your developer will deploy your custom configuration</span>
                                        </li>
                                    </ol>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>

            {/* Image Cropper Modal */}
            {showCropper && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4">
                    <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-2xl">
                        <div className="p-6">
                            <h3 className="text-xl font-semibold text-secondary mb-4">Crop Your Logo</h3>

                            <div className="relative h-96 bg-gray-100 rounded-lg mb-4">
                                <Cropper
                                    image={imageSrc}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={1}
                                    onCropChange={setCrop}
                                    onZoomChange={setZoom}
                                    onCropComplete={onCropComplete}
                                />
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-secondary mb-2">
                                    Zoom
                                </label>
                                <input
                                    type="range"
                                    min={1}
                                    max={3}
                                    step={0.1}
                                    value={zoom}
                                    onChange={(e) => setZoom(e.target.value)}
                                    className="w-full"
                                />
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={createCroppedImage}
                                    className="flex-1 px-6 py-3 bg-primary rounded-lg font-medium hover:opacity-90 transition-all duration-200" style={{color: 'var(--color-surface)'}}
                                >
                                    Apply Crop
                                </button>
                                <button
                                    onClick={() => setShowCropper(false)}
                                    className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-all duration-200"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default LogoPicker;
